/**
 * Chummys product tabs — <chummys-tabs>
 * Tablist behaviour plus the sliding highlight behind the active pill.
 * Panels are all in the DOM already; this only toggles `hidden`.
 */
(() => {
  if (customElements.get('chummys-tabs')) return;

  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

  class ChummysTabs extends HTMLElement {
    constructor() {
      super();
      this.index = 0;
      this.onResize = this.onResize.bind(this);
    }

    connectedCallback() {
      this.switcher = this.querySelector('.chummys-tabs__switch');
      this.scroller = this.querySelector('.chummys-tabs__switch-wrap');
      this.highlight = this.querySelector('.chummys-tabs__highlight');
      this.tabs = Array.from(this.querySelectorAll('.chummys-tabs__tab'));
      this.panels = Array.from(this.querySelectorAll('.chummys-tabs__panel'));
      if (!this.panels.length) return;

      this.index = clamp(parseInt(this.dataset.default || '0', 10) || 0, 0, this.panels.length - 1);

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.activate(Number(tab.dataset.index), { user: true }));
      });
      if (this.switcher) this.switcher.addEventListener('keydown', (e) => this.onKey(e));

      this.setActiveState(this.index);
      this.moveHighlight(true);

      window.addEventListener('resize', this.onResize, { passive: true });
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.moveHighlight(true)).catch(() => {});
      }
      // images loading in the open panel can reflow the pill
      requestAnimationFrame(() => this.moveHighlight(true));

      this.bindDesignMode();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
    }

    activate(i, opts = {}) {
      if (!this.panels[i] || i === this.index) return;
      this.index = i;
      this.setActiveState(i);
      this.moveHighlight();
      if (opts.focus && this.tabs[i]) this.tabs[i].focus({ preventScroll: true });
      this.scrollTabIntoView(i);
      this.dispatchEvent(new CustomEvent('chummys:tabs:change', {
        bubbles: true,
        detail: { index: i, label: this.tabs[i] ? this.tabs[i].textContent.trim() : '' }
      }));
    }

    setActiveState(i) {
      this.panels.forEach((panel, k) => {
        const on = k === i;
        panel.classList.toggle('is-active', on);
        panel.hidden = !on;
      });
      this.tabs.forEach((tab, k) => {
        const on = k === i;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
      });
    }

    moveHighlight(immediate = false) {
      const tab = this.tabs[this.index];
      if (!tab || !this.highlight || !this.tabs.length) return;
      const first = this.tabs[0];
      if (immediate) this.highlight.style.transition = 'none';
      this.highlight.style.width = `${tab.offsetWidth}px`;
      this.highlight.style.transform = `translateX(${tab.offsetLeft - first.offsetLeft}px)`;
      if (immediate) {
        void this.highlight.offsetHeight;
        this.highlight.style.transition = '';
      }
    }

    // keep the chosen pill visible when the switcher scrolls sideways on phones
    scrollTabIntoView(i) {
      const tab = this.tabs[i];
      if (!tab || !this.scroller) return;
      if (this.scroller.scrollWidth <= this.scroller.clientWidth) return;
      const target = tab.offsetLeft - (this.scroller.clientWidth - tab.offsetWidth) / 2;
      this.scroller.scrollTo({
        left: Math.max(0, target),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    }

    onKey(event) {
      const map = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
      const n = this.panels.length;
      if (event.key in map) {
        event.preventDefault();
        this.activate((this.index + map[event.key] + n) % n, { user: true, focus: true });
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.activate(0, { user: true, focus: true });
      } else if (event.key === 'End') {
        event.preventDefault();
        this.activate(n - 1, { user: true, focus: true });
      }
    }

    onResize() { this.moveHighlight(true); }

    bindDesignMode() {
      if (!window.Shopify || !window.Shopify.designMode) return;
      this.addEventListener('shopify:block:select', (event) => {
        const tab = event.target.closest('.chummys-tabs__tab');
        if (tab) this.activate(Number(tab.dataset.index));
      });
    }
  }

  customElements.define('chummys-tabs', ChummysTabs);
})();
